import { useEffect, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import type { Chapter } from '../content/chapters';
import { loadAudio, play } from '../audio/player';

/** The voiced comic "spine": an intro card sets the scene when a chapter starts, a victory
 *  card celebrates beating its villain. The narration AUTO-PLAYS (he listens — it's not
 *  reading), so the story can be richer than the decodable beat text. On-card text is
 *  minimal (chapter / villain / team) and never gates advancing. */
export function StoryCard({
  kind,
  chapter,
  onContinue,
}: {
  kind: 'intro' | 'victory';
  chapter: Chapter;
  onContinue: () => void;
}) {
  const isVictory = kind === 'victory';
  const lineId = isVictory ? chapter.victoryLineId : chapter.introLineId;

  const speak = () =>
    void (async () => {
      await loadAudio();
      await play(lineId);
    })();

  // Auto-play the narration once when the card appears.
  useEffect(() => {
    speak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  return (
    <div style={screen}>
      <div style={kicker}>{isVictory ? '🎉 CHAPTER CLEARED' : `CHAPTER ${chapter.order}`}</div>
      <div style={title}>{isVictory ? `${chapter.villain.name} — beaten!` : chapter.title}</div>

      <motion.div
        key={lineId}
        initial={{ scale: 0.5, rotate: isVictory ? 0 : -8 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 15 }}
        style={artWrap}
      >
        <VillainArt id={chapter.villain.id} emoji={chapter.villain.emoji} dim={isVictory} />
        {isVictory && <span style={boom}>💥</span>}
      </motion.div>

      <div style={villainName}>
        {chapter.villain.emoji} {chapter.villain.name}
      </div>
      <div style={sub}>
        {chapter.team} · {chapter.location}
      </div>

      <div style={row}>
        <button type="button" style={hearBtn} onClick={speak}>
          🔊 Hear it again
        </button>
        <button type="button" style={cta} onClick={onContinue}>
          {isVictory ? 'Next chapter →' : "Let's go! ▶"}
        </button>
      </div>
    </div>
  );
}

/** Villain pixel-art from public/villains/<id>.png, falling back to the emoji. */
function VillainArt({ id, emoji, dim }: { id: string; emoji: string; dim?: boolean }) {
  const [failed, setFailed] = useState(false);
  const filter = dim ? 'grayscale(1) brightness(0.65)' : undefined;
  if (failed) return <span style={{ fontSize: 130, lineHeight: 1, filter }}>{emoji}</span>;
  return (
    <img
      src={`${import.meta.env.BASE_URL}villains/${id}.png`}
      width={150}
      height={150}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
      style={{ imageRendering: 'pixelated', filter }}
    />
  );
}

const screen: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  background: 'var(--frame)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  textAlign: 'center',
};
const kicker: CSSProperties = { fontSize: 18, fontWeight: 800, letterSpacing: 2, color: 'var(--gold)' };
const title: CSSProperties = {
  fontFamily: 'var(--font-read)',
  fontSize: 'clamp(32px, 6vmin, 60px)',
  fontWeight: 800,
  color: '#f4f4f4',
};
const artWrap: CSSProperties = { position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: '6px 0' };
const boom: CSSProperties = { position: 'absolute', fontSize: 96, filter: 'drop-shadow(0 0 8px rgba(255,210,74,0.8))' };
const villainName: CSSProperties = { fontSize: 26, fontWeight: 800, color: 'var(--spidey-red)' };
const sub: CSSProperties = { fontSize: 18, fontWeight: 700, color: '#cdd6ee' };
const row: CSSProperties = { display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 };
const hearBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 22px',
  borderRadius: 999,
  border: '2px solid var(--gold)',
  background: 'transparent',
  color: 'var(--gold)',
  fontSize: 18,
  fontWeight: 800,
  cursor: 'pointer',
};
const cta: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 40px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--spidey-red)',
  color: '#fff',
  fontSize: 22,
  fontWeight: 800,
  cursor: 'pointer',
};
